import { state } from '__common/store';
import { getPixiLayer } from '__editor/googleMapsRoofsSelector/components/stage/stage';
import { ROOF_VERTEX_SNAP_THRESHOLD_PX } from '__editor/components/roofsSelector/components/roofsSelectorDrawingManager/roofsSelectorDrawingManagerConfigs';
import { snapIndicatorModel } from '__editor/googleMapsRoofsSelector/models/snapIndicator';

const ROOF_CORNER_SNAP_INDICATOR_ID = 'roofCornerSnapIndicator';

export interface IRoofCorner {
  x: number;
  y: number;
  roofId: number;
  cornerIndex: number;
}

let activeSnappedCorner: IRoofCorner | null = null;

export function getActiveSnappedCorner(): IRoofCorner | null {
  return activeSnappedCorner;
}

export function clearActiveSnappedCorner() {
  activeSnappedCorner = null;
}

// Collects every corner of every already-drawn roof and projects it into the
// current stage pixel space so it can be compared against the live cursor.
// `excludeRoofId` skips a roof's own corners - used while reshaping an existing
// roof so its dragged vertex never snaps onto another corner of the same roof.
export function getExistingRoofCornersInPixels(excludeRoofId?: number): IRoofCorner[] {
  const { drawingManager: { roofs } } = state();
  const pixiLayer = getPixiLayer();

  if ( !pixiLayer || !roofs) {
    return [];
  }

  const corners: IRoofCorner[] = [];

  Object.keys(roofs).forEach((roofId) => {
    if (excludeRoofId !== undefined && Number(roofId) === Number(excludeRoofId)) {
      return;
    }

    const roof = roofs[roofId];
    if (!roof || !roof.coords) {
      return;
    }

    roof.coords.forEach((coord, cornerIndex) => {
      const pixel = pixiLayer.fromLatLngToPixel(coord);
      corners.push({ x: pixel.x, y: pixel.y, roofId: Number(roofId), cornerIndex });
    });
  });

  return corners;
}

// Returns the nearest existing roof corner to `point` if one lies within the
// snap threshold, snapping the point onto it. Otherwise returns `point` as-is.
// The matched corner (if any) is cached for the snap indicator + shared-edge logic.
export function snapToRoofCorner(point: pixelPoint): pixelPoint {
  const corners = getExistingRoofCornersInPixels();

  if (!corners.length) {
    activeSnappedCorner = null;
    return point;
  }

  let nearest: IRoofCorner | null = null;
  let nearestDistance = ROOF_VERTEX_SNAP_THRESHOLD_PX;

  corners.forEach((corner) => {
    const dx = corner.x - point.x;
    const dy = corner.y - point.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= nearestDistance) {
      nearestDistance = distance;
      nearest = corner;
    }
  });

  if (nearest) {
    activeSnappedCorner = nearest;
    return { x: nearest.x, y: nearest.y };
  }

  activeSnappedCorner = null;
  return point;
}

// While reshaping an existing roof (dragging one of its corner markers), snap
// the dragged vertex onto a nearby corner of ANOTHER roof. Returns the snapped
// position as a google.maps.LatLng when within the threshold, otherwise null
// (the caller then keeps the raw drag position). The matched corner is cached so
// `renderRoofCornerSnapIndicator` can highlight it, mirroring the draw-time snap.
export function snapRoofCornerDuringEdit(latLng: google.maps.LatLng, roofId: number) {
  const pixiLayer: any = getPixiLayer();
  if (!pixiLayer) {
    activeSnappedCorner = null;
    return null;
  }

  const point = pixiLayer.fromLatLngToPixel({ lat: latLng.lat(), lng: latLng.lng() });
  const corners = getExistingRoofCornersInPixels(roofId);

  let nearest: IRoofCorner | null = null;
  let nearestDistance = ROOF_VERTEX_SNAP_THRESHOLD_PX;

  corners.forEach((corner) => {
    const dx = corner.x - point.x;
    const dy = corner.y - point.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= nearestDistance) {
      nearestDistance = distance;
      nearest = corner;
    }
  });

  if (nearest) {
    activeSnappedCorner = nearest;
    return pixiLayer.fromPixelToLatLng({ x: nearest.x, y: nearest.y });
  }

  activeSnappedCorner = null;
  return null;
}

// Draws (or refreshes) the snap highlight ring over the currently snapped
// corner on the google-maps pixi overlay, so the user can SEE the snap while
// reshaping a roof - the corner-drag path renders this itself because it runs
// outside the draw-time render loop.
export function renderRoofCornerSnapIndicator() {
  const pixiLayer: any = getPixiLayer();
  if (!pixiLayer || !pixiLayer.stage) {
    return;
  }

  pixiLayer.stage.children = pixiLayer.stage.children
    .filter(child => child.id !== ROOF_CORNER_SNAP_INDICATOR_ID);

  const corner = getActiveSnappedCorner();
  if (corner) {
    const scale = pixiLayer.stage.scale.x;
    const indicator = snapIndicatorModel(corner, scale);
    indicator.id = ROOF_CORNER_SNAP_INDICATOR_ID;
    pixiLayer.stage.addChild(indicator);
  }

  pixiLayer.rerender();
}

// Clears the reshape snap state + its highlight (call on drag end).
export function clearRoofCornerSnapIndicator() {
  clearActiveSnappedCorner();
  renderRoofCornerSnapIndicator();
}
